import { useId, useState, type KeyboardEvent } from 'react';

interface TokenListEditorProps {
  label: string;
  hint?: string;
  placeholder?: string;
  tokens: readonly string[];
  onChange: (tokens: string[]) => void;
  disabled?: boolean;
  addLabel?: string;
}

/**
 * Free-entry list of short strings rendered as removable chips — never-say
 * terms, product benefits, asset tags, custom goals.
 *
 * Enter adds a token and never submits the surrounding form.
 */
export function TokenListEditor({
  label,
  hint,
  placeholder,
  tokens,
  onChange,
  disabled = false,
  addLabel = 'Add',
}: TokenListEditorProps) {
  const inputId = useId();
  const [draft, setDraft] = useState('');

  function addToken() {
    const value = draft.trim();
    if (!value || tokens.includes(value)) {
      setDraft('');
      return;
    }
    onChange([...tokens, value]);
    setDraft('');
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      addToken();
    }
  }

  return (
    <div className="field">
      <label className="field__label" htmlFor={inputId}>
        {label}
      </label>
      {hint ? <p className="field__hint">{hint}</p> : null}

      <div className="token-editor">
        <input
          className="field__input token-editor__input"
          id={inputId}
          type="text"
          value={draft}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
        />
        <button className="button" type="button" onClick={addToken} disabled={disabled}>
          {addLabel}
        </button>
      </div>

      {tokens.length > 0 ? (
        <ul className="token-list">
          {tokens.map((token) => (
            <li className="token" key={token}>
              <span>{token}</span>
              <button
                className="token__remove"
                type="button"
                aria-label={`Remove ${token}`}
                disabled={disabled}
                onClick={() => onChange(tokens.filter((entry) => entry !== token))}
              >
                &times;
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
